import React, { useCallback } from 'react';
import { Box } from '@mui/material';
import { colors, Typography } from '@sweep-io/sweep-design';
import { TruncatedTextTooltip } from '../TruncatedTextTooltip';
import { TableIllustration } from './ArtifactIllustrations';
import { useTableArtifactsOptional } from './useTableArtifacts';
import { ARTIFACT_PREVIEW_NARROW_QUERY } from '../../aiAgentCenter/aiAgentsConsts';
import useAgentsHome from '../../aiAgentCenter/useAgentsHome';

interface TableArtifactPreviewProps {
  tableId: string;
}

export const TableArtifactPreview: React.FC<TableArtifactPreviewProps> = ({ tableId }) => {
  const tableArtifactContext = useTableArtifactsOptional();
  const isLoading = tableArtifactContext?.isLoading ?? false;
  const { isExpanded, setIsExpanded } = useAgentsHome();

  const handleViewDetails = useCallback(() => {
    if (tableArtifactContext) {
      // If in collapsed mode, expand first, then open panel
      if (!isExpanded) {
        setIsExpanded({ isExpanded: true });
      }
      tableArtifactContext.openTablePanel(tableId);
    }
  }, [tableArtifactContext, tableId, isExpanded, setIsExpanded]);

  return (
    <Box
      sx={{
        containerType: 'inline-size',
        border: `1px solid ${colors.grey[300]}`,
        borderRadius: '12px',
        padding: 2,
        backgroundColor: 'white',
        width: '100%',
        height: '78px',
        transition: 'all 0.2s ease-in-out',
        cursor: !tableArtifactContext ? 'default' : isLoading ? 'wait' : 'pointer',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          borderColor: colors.blue[500],
        },
      }}
      onClick={handleViewDetails}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
          paddingRight: '240px',
          [ARTIFACT_PREVIEW_NARROW_QUERY]: {
            paddingRight: 0,
          },
        }}
      >
        <TruncatedTextTooltip variant="body">Table Data</TruncatedTextTooltip>
        <Typography variant="caption" color={colors.grey[700]}>
          Data table
        </Typography>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          right: '20px',
          bottom: '-7px',
          [ARTIFACT_PREVIEW_NARROW_QUERY]: {
            display: 'none',
          },
        }}
      >
        <TableIllustration />
      </Box>
    </Box>
  );
};
